/*
  industrialkonzept homepage, 2026-09-23
  Reveal on scroll, live index counters, cover stories, room preview cursor, product rails.
  Everything degrades to a plain, fully usable page without this script.
*/
(() => {
  if (window.__ikHome) return;
  window.__ikHome = true;

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const designMode = !!(window.Shopify && window.Shopify.designMode);

  // How many designers the shop carries: the same data as the designers A–Z page
  // (/collections/all?view=ikn-designer-data, 250 products per page), shared in this browser for a day.
  // The shop's own designer filter only hands out 100 names, so it cannot be counted on the server.
  // A first visit asks for about a dozen pages; that waits until the page itself has loaded, so it
  // never competes with the cover picture and the styles.
  const afterLoad = () =>
    new Promise((done) => {
      if (document.readyState === 'complete') done();
      else window.addEventListener('load', done, { once: true });
    });
  let designersP = null;
  const designerCount = () => {
    if (designersP) return designersP;
    const KEY = 'ikn-az-v1';
    try {
      const hit = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (hit && Date.now() - hit.t < 24 * 3600 * 1000) return (designersP = Promise.resolve(Object.keys(hit.d).length));
    } catch (e) {}
    const base = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'collections/all?view=ikn-designer-data&page=';
    const get = (p) => fetch(base + p).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    designersP = afterLoad()
      .then(() => get(1))
      .then((first) => {
        if (!first) return null;
        const rest = [];
        for (let p = 2; p <= first.pages; p++) rest.push(get(p));
        return Promise.all(rest).then((more) => [first, ...more]);
      })
      .then((pages) => {
        if (!pages || pages.some((pg) => !pg)) return 0; // incomplete: show the brands only, remember nothing
        const d = {};
        pages.forEach((pg) => Object.entries(pg.d).forEach(([k, v]) => (d[k] = (d[k] || 0) + v)));
        try {
          localStorage.setItem(KEY, JSON.stringify({ t: Date.now(), d }));
        } catch (e) {}
        return Object.keys(d).length;
      })
      .catch(() => 0);
    return designersP;
  };

  // Everything below works on one part of the page, so the theme editor can set up a section again
  // after it redrew it (shopify:section:load)
  const init = (scope) => {
    /* Reveal */
    const reveals = scope.querySelectorAll('.ikh-reveal');
    // no fade-in in the theme editor: a section redrawn there would stay hidden
    if ('IntersectionObserver' in window && !reduced && !designMode) {
      root.classList.add('ikh-js');
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
      reveals.forEach((el) => io.observe(el));
      // Safety net: never leave content hidden
      window.setTimeout(() => reveals.forEach((el) => el.classList.add('is-in')), 6000);
    }

    /* Counters */
    const counters = scope.querySelectorAll('[data-ikh-count]');
    const formatter = new Intl.NumberFormat(root.lang || undefined);
    counters.forEach((el) => {
      const value = parseInt(el.getAttribute('data-ikh-count'), 10);
      if (value) el.textContent = formatter.format(value) + (el.getAttribute('data-ikh-suffix') || '');
    });
    // "Brands and designers": the number the server wrote is the brands, the designers are added here
    scope.querySelectorAll('[data-ikh-plus-designers]').forEach((el) => {
      el.__ikhWait = designerCount().then((n) => {
        if (!n) return;
        const total = (parseInt(el.getAttribute('data-ikh-plus-designers'), 10) || 0) + n;
        const counts = el.hasAttribute('data-ikh-count');
        if (counts) el.setAttribute('data-ikh-count', total);
        // counters count up to it themselves; if one already ran (slow data), just show the total
        if (!counts || reduced || el.__ikhRan) el.textContent = formatter.format(total) + (el.getAttribute('data-ikh-suffix') || '');
      });
    });
    const runCounter = (el) => {
      // wait for the designers (at most 3 s), so the number counts up once to the right value
      if (el.__ikhWait) {
        const wait = el.__ikhWait;
        el.__ikhWait = null;
        Promise.race([wait, new Promise((r) => setTimeout(r, 3000))]).then(() => runCounter(el));
        return;
      }
      el.__ikhRan = true;
      // read on every frame: designers that arrive while it counts raise the goal instead of being
      // overwritten by the next frame
      const target = () => parseInt(el.getAttribute('data-ikh-count'), 10) || 0;
      if (!target() || reduced) return;
      const suffix = el.getAttribute('data-ikh-suffix') || '';
      const start = performance.now();
      const duration = 1400;
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = formatter.format(Math.round(target() * eased)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if ('IntersectionObserver' in window) {
      const cio = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              runCounter(entry.target);
              cio.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.6 }
      );
      counters.forEach((el) => cio.observe(el));
    }

    /* Room preview that follows the cursor */
    scope.querySelectorAll('[data-ikh-rooms]').forEach((section) => {
      if (section.__ikh) return;
      section.__ikh = true;
      const cursor = section.querySelector('.ikh-cursor');
      const list = section.querySelector('.ikh-rooms__list');
      if (!cursor || !list || !finePointer || reduced) return;
      const images = cursor.querySelectorAll('img');
      let x = 0;
      let y = 0;
      let cx = 0;
      let cy = 0;
      let raf = null;
      const loop = () => {
        cx += (x - cx) * 0.18;
        cy += (y - cy) * 0.18;
        cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0) translate(-50%, -50%) scale(1)`;
        raf = Math.abs(x - cx) + Math.abs(y - cy) > 0.3 ? requestAnimationFrame(loop) : null;
      };
      list.addEventListener('mousemove', (e) => {
        x = e.clientX + 40;
        y = e.clientY;
        if (!raf) raf = requestAnimationFrame(loop);
      });
      list.querySelectorAll('[data-ikh-room]').forEach((link) => {
        link.addEventListener('mouseenter', (e) => {
          const index = link.getAttribute('data-ikh-room');
          images.forEach((img) => img.classList.toggle('is-active', img.getAttribute('data-ikh-room') === index));
          if (!cursor.classList.contains('is-on')) {
            cx = x = e.clientX + 40;
            cy = y = e.clientY;
          }
          cursor.classList.add('is-on');
        });
      });
      list.addEventListener('mouseleave', () => cursor.classList.remove('is-on'));
      window.addEventListener('scroll', () => cursor.classList.remove('is-on'), { passive: true });
    });

    /* Cover stories: up to three stories take turns like Instagram stories (sections/ik-home-cover.liquid).
       The line of the running story fills by itself (CSS animation ikh-seg in ik-shop.css, as long as the
       section setting says); when it is full, the next one comes. It pauses while
       keyboard focus is inside the cover, while the cover is out of view or the tab is hidden, and with
       the pause button; with reduced motion too (only the zoom and the rising title are left out). A tap on a line
       jumps to that story, a swipe on phones goes to the next or previous one. The pictures of stories 2
       and 3 load after the page, one story ahead. */
    scope.querySelectorAll('[data-ikh-stories]').forEach((cover) => {
      if (cover.__ikh) return;
      cover.__ikh = true;
      const stories = [...cover.querySelectorAll('.ikh-cover__story')];
      const slides = [...cover.querySelectorAll('.ikh-cover__slide')];
      const segs = [...cover.querySelectorAll('.ikh-cover__seg')];
      const links = [...cover.querySelectorAll('[data-ikh-cover-link]')];
      const pause = cover.querySelector('.ikh-cover__pause');
      const bar = cover.querySelector('.ikh-cover__progress');
      const n = stories.length;
      if (n < 2 || !bar) return;
      let current = 0;

      const load = (k) => {
        const pic = slides.find((s) => Number(s.dataset.ikhSlide) === k);
        if (!pic) return;
        pic.querySelectorAll('[data-srcset]').forEach((el) => {
          el.srcset = el.dataset.srcset;
          el.removeAttribute('data-srcset');
        });
      };

      // held: why the stories stand still (pause button, keyboard focus, out of view, hidden tab)
      const why = new Set();

      const show = (k) => {
        current = (k + n) % n;
        slides.forEach((s) => s.classList.toggle('is-active', Number(s.dataset.ikhSlide) === current));
        stories.forEach((s, j) => {
          s.classList.toggle('is-active', j === current);
          s.setAttribute('aria-hidden', j === current ? 'false' : 'true');
        });
        segs.forEach((s, j) => {
          s.classList.toggle('is-active', j === current);
          s.classList.toggle('is-done', j < current);
          s.classList.remove('is-running');
          if (j === current) s.setAttribute('aria-current', 'true');
          else s.removeAttribute('aria-current');
        });
        // start the line of the new story from empty (the reflow lets the animation begin again)
        void segs[current].offsetWidth;
        segs[current].classList.add('is-running');
        const url = stories[current].dataset.url;
        if (url) links.forEach((a) => (a.href = url));
        load(current);
        load(current + 1);
      };

      // a full line brings the next story; a paused line (is-paused on the cover) simply stands still
      cover.addEventListener('animationend', (e) => {
        if (e.animationName === 'ikh-seg' && e.target === segs[current].firstElementChild) show(current + 1);
      });
      const sync = () => cover.classList.toggle('is-paused', why.size > 0);
      const hold = (reason, on) => {
        if (on) why.add(reason);
        else why.delete(reason);
        sync();
      };
      const setUser = (on) => {
        hold('user', on);
        pause.setAttribute('aria-pressed', String(on));
        pause.setAttribute('aria-label', on ? bar.dataset.labelPlay : bar.dataset.labelPause);
      };
      if (designMode) setUser(true);

      segs.forEach((seg, j) => seg.addEventListener('click', () => show(j)));
      pause.addEventListener('click', () => setUser(!why.has('user')));

      // no pause on mouse over: the cover fills most of the screen, so the mouse nearly always rests on
      // it and the stories stood still on computers. Keyboard focus inside the cover does pause.
      cover.addEventListener('focusin', (e) => {
        // iOS before 15.4 does not know :focus-visible and would throw here
        let fv = false;
        try {
          fv = e.target.matches(':focus-visible');
        } catch (x) {
          /* treat as no keyboard focus */
        }
        hold('focus', fv && !e.target.closest('.ikh-cover__progress'));
      });
      cover.addEventListener('focusout', () => hold('focus', false));
      document.addEventListener('visibilitychange', () => hold('tab', document.hidden));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => entries.forEach((en) => hold('view', !en.isIntersecting)), { threshold: 0.25 }).observe(cover);
      }
      show(0);

      // phones: swipe on the picture for the next or previous story (the product row keeps its own swipe)
      let sx = 0;
      let sy = 0;
      cover.addEventListener('touchstart', (e) => {
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      cover.addEventListener('touchend', (e) => {
        if (e.target.closest('.ikn-covershop')) return;
        const dx = e.changedTouches[0].clientX - sx;
        const dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 50 && Math.abs(dy) < 40) show(current + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // story 2's picture once the page has loaded, story 3's when story 2 shows
      const soon = () => (window.requestIdleCallback ? window.requestIdleCallback(() => load(1), { timeout: 2000 }) : window.setTimeout(() => load(1), 800));
      if (document.readyState === 'complete') soon();
      else window.addEventListener('load', soon, { once: true });
    });

    /* Product rails: arrows, drag, progress */
    scope.querySelectorAll('[data-ikh-rail]').forEach((rail) => {
      if (rail.__ikh) return;
      rail.__ikh = true;
      const track = rail.querySelector('.ikh-rail__track');
      const prev = rail.querySelector('[data-ikh-prev]');
      const next = rail.querySelector('[data-ikh-next]');
      const bar = rail.querySelector('.ikh-rail__bar');
      if (!track) return;

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const ratio = max > 0 ? track.scrollLeft / max : 0;
        if (bar) {
          const width = Math.max(0.12, track.clientWidth / track.scrollWidth);
          bar.style.width = `${width * 100}%`;
          bar.style.transform = `translateX(${ratio * ((1 - width) / width) * 100}%)`;
        }
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      };

      const step = () => {
        const card = track.querySelector('.ikh-card');
        return card ? card.getBoundingClientRect().width * 2 : track.clientWidth * 0.8;
      };

      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' }));
      let frame = 0;
      const later = () => {
        if (!frame) frame = requestAnimationFrame(() => ((frame = 0), update()));
      };
      track.addEventListener('scroll', later, { passive: true });
      window.addEventListener('resize', later);
      update();

      if (!finePointer) return;
      let down = false;
      let moved = false;
      let startX = 0;
      let startLeft = 0;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          // Let the click that ends a drag fall through without opening a product
          window.setTimeout(() => track.classList.remove('is-dragging'), 0);
        }
      });
      track.addEventListener('dragstart', (e) => e.preventDefault());
    });
  };
  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
